import React, { forwardRef } from 'react';
import classNames from 'classnames';
import './IconButton.less';
import { Icon, IconSize } from '../icons/Icon';
import { Spinner } from 'components/Spinner';
import { SpringValues, PickAnimated } from 'react-spring';

export interface IIconButtonProps extends React.ComponentProps<'button'> {
  iconClassName: string;
  iconSize?: IconSize;
  busy?: boolean;
  iconStyle?: SpringValues<PickAnimated<any>>;
}

const getClassName = (className?: string, busy?: boolean) =>
  classNames('dpc-icon-button', className, {
    'dpc-icon-button--busy': busy,
  });

/**
 * Icon button to standardize certain behaviors
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export const IconButton = forwardRef(
  (
    {
      busy,
      className,
      disabled,
      iconClassName,
      iconSize,
      iconStyle,
      title,
      onClick,
      children,
      ...htmlOptions
    }: IIconButtonProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    return (
      <button
        ref={ref}
        title={title}
        className={getClassName(className, busy)}
        onClick={onClick}
        disabled={disabled || busy}
        {...htmlOptions}
      >
        {busy ? (
          <Spinner size={getSpinnerSize(iconSize)} className="dpc-icon-button__spinner" />
        ) : (
          <>
            <Icon className={iconClassName} size={iconSize} animationStyle={iconStyle} />
            {children}
          </>
        )}
      </button>
    );
  }
);

function getSpinnerSize(iconSize?: IconSize) {
  // (from  Icon.less)
  switch (iconSize) {
    case 'small':
      return '0.875rem'; //14px;
    case 'input':
      return '1rem'; // 16px;
    case 'large':
      return '1.25rem'; // 22px;
    case 'native':
      return '1.5rem'; // 24px; // artifact of poor communication. native size in 24px box.
    case 'normal':
    default:
      return '1.125rem'; //18px;
  }
}
